import { inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
  HttpInterceptorFn
} from '@angular/common/http';
import { Observable, throwError, catchError, timeout } from 'rxjs';
import { JwtService } from '../services/jwt.service';

/** URLs excluded from JWT interception */
const EXCLUDED_URL_PATTERNS = [
  '/auth/login',
  '/auth/logout',
  '/auth/refresh',
];

/** Track redirect state to prevent multiple simultaneous redirects */
let redirectInProgress = false;

/**
 * Functional JWT interceptor for Angular 17+
 * - Adds Bearer token to all non-auth requests
 * - Handles 401/403 errors with automatic redirect
 * - Adds a 30s timeout to all requests
 */
export const jwtInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const jwtService = inject(JwtService);

  if (EXCLUDED_URL_PATTERNS.some(url => request.url.includes(url))) {
    return next(request);
  }

  const token = jwtService.getToken();

  if (!token) {
    return next(request);
  }

  const modifiedRequest = request.clone({
    setHeaders: { Authorization: `Bearer ${token}` }
  });

  return next(modifiedRequest).pipe(
    timeout(30000),
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        handleUnauthorized(jwtService);
      }
      return throwError(() => error);
    })
  );
};

function handleUnauthorized(jwtService: JwtService): void {
  if (redirectInProgress) {
    return;
  }

  redirectInProgress = true;
  jwtService.removeToken();
  jwtService.redirectLoginPage();

  // Reset flag after a delay to allow new redirects if the page doesn't actually navigate
  setTimeout(() => {
    redirectInProgress = false;
  }, 5000);
}
